'use client';

import React, { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { PauseIcon, PlayIcon } from '@heroicons/react/24/solid';
import { useTranslations } from 'next-intl';
import { twJoin } from 'tailwind-merge';
import { PieceFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { VideoSource } from '@/utils/video-source';
import { VideoPlayer } from '@/ui/components/shared/video-player/video-player';
import { VideoControlButton } from '@/ui/components/shared/video-player/video-player-controls';
import { getVideoAriaLabel, parseVideoSeo } from '@/utils/video-seo';

const SLOW_EFFECTIVE_TYPES = new Set(['slow-2g', '2g']);

const DEFAULT_ARIA_LABEL = 'Background video';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * A decorative video is never worth the bytes on a metered or very slow connection — the hero
 * still looks complete because the poster image stays behind it.
 */
function isBandwidthConstrained(): boolean {
  const { connection } = navigator;

  if (!connection) {
    return false;
  }

  if (connection.saveData) {
    return true;
  }

  return SLOW_EFFECTIVE_TYPES.has(connection.effectiveType ?? '');
}

/**
 * An auto-playing loop is exactly the motion a visitor with the "reduce motion" setting asked
 * to be spared, so they keep the still poster instead.
 */
function prefersReducedMotion(): boolean {
  if (typeof globalThis.matchMedia !== 'function') {
    return false;
  }

  return globalThis.matchMedia(REDUCED_MOTION_QUERY).matches;
}

/** The video is skipped whenever it would cost the visitor bytes or motion they asked to avoid. */
function shouldSkipVideo(): boolean {
  return isBandwidthConstrained() || prefersReducedMotion();
}

export interface VideoHeroBackgroundProps {
  source: VideoSource;
  videoSeo?: PieceFragment | null;
  shouldLoop?: boolean;
  /** Corner the pause/play button sits in, at the frame's own inset. */
  playbackButtonClasses?: string;
}

export function VideoHeroBackground({
  source,
  videoSeo,
  shouldLoop,
  playbackButtonClasses,
}: Readonly<VideoHeroBackgroundProps>) {
  const t = useTranslations('Video');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isStopped, setIsStopped] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  // Server and first client render must agree, so the guard only resolves inside the effect.
  const [isPlayerAllowed, setIsPlayerAllowed] = useState(true);

  useEffect(() => {
    setIsPlayerAllowed(!shouldSkipVideo());
  }, []);

  if (!isPlayerAllowed) {
    return null;
  }

  const seo = parseVideoSeo(videoSeo);

  // The label follows the real playback state, so a video that ended by itself offers to play
  // again. Starting it needs the player to see `isActive` go low and high: it only reacts to the
  // change, and a video that ran out was never switched off in the first place.
  const togglePlayback = () => {
    if (isPlaying) {
      setIsStopped(true);
      return;
    }

    flushSync(() => setIsStopped(true));
    setIsStopped(false);
  };

  return (
    <>
      <div
        // The video repeats what the hero title already says, so it stays out of the a11y tree.
        aria-hidden="true"
        data-testid="video-hero-background"
        className={twJoin(
          'absolute inset-0 -z-10 transition-opacity duration-700',
          isPlaying ? 'opacity-100' : 'opacity-0',
        )}
      >
        <VideoPlayer
          source={source}
          ariaLabel={getVideoAriaLabel(seo, DEFAULT_ARIA_LABEL)}
          mode="background"
          showControls={false}
          loop={shouldLoop}
          isActive={!isStopped}
          onPlayingChange={setIsPlaying}
          onFatalError={() => setHasFailed(true)}
          className="h-full w-full"
        />
      </div>
      {/* Outside the aria-hidden layer, because stopping the motion has to stay reachable by
          keyboard and screen reader. A video that failed for good leaves nothing to stop. */}
      {!hasFailed && (
        <div
          data-testid="video-hero-playback-button"
          className={twJoin('absolute z-10', playbackButtonClasses)}
        >
          <VideoControlButton
            label={isPlaying ? t('pause') : t('play')}
            onClick={togglePlayback}
            Icon={isPlaying ? PauseIcon : PlayIcon}
          />
        </div>
      )}
    </>
  );
}
